<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import { breakpoints } from '@oat-sa-private/ui-identity';

    /**
     * @typedef {Object} Props
     * @property {string} [logoSrc]
     * @property {string} [logoLinkUrl]
     * @property {string} [logoAltText]
     * @property {Object[]} [startActions]
     * @property {Object[]} [endActions]
     * @property {Number} [maxEndActions]
     * @property {string?} [iconSize]
     * @property {Function} [onAction]
     * @property {import('svelte').Snippet} [children]
     * @property {import('svelte').Snippet} [asideAfter]
     */

    /** @type {Props} */
    let {
        logoSrc = '',
        logoLinkUrl = '',
        logoAltText = 'logo',
        startActions = [],
        endActions = [],
        maxEndActions = endActions.length,
        iconSize,
        onAction = () => {},
        children,
        asideAfter
    } = $props();

    let constrainedMaxEndActions = $derived.by(() => {
        if (maxEndActions > endActions.length || maxEndActions < 0) {
            return endActions.length;
        }
        return maxEndActions;
    });

    let visibleEndActions = $derived(endActions.slice(0, constrainedMaxEndActions));
    let hiddenEndActions = $derived(endActions.slice(constrainedMaxEndActions));

    let windowWidth = $state();

    // Choose the button size based on window width
    let iconSizeResult = $derived.by(() => {
        if (iconSize) {
            return iconSize;
        }
        return windowWidth <= breakpoints.width.medium ? '16' : '24';
    });

    let hiddenActionsExpanded = $state(false);

    function toggleActionSubmenu(e) {
        hiddenActionsExpanded = !hiddenActionsExpanded;
        e.preventDefault();
        e.stopPropagation();
    }

    function closeActionSubmenu() {
        hiddenActionsExpanded = false;
    }

    function emitAction(key) {
        onAction({ key });
    }
</script>

<style>
    .headerbar {
        --headerbar-default-height: 6rem;
        --headerbar-default-background: var(--color-bg-default);
        --headerbar-default-side-padding: 0.25rem;

        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        width: 100%;
        height: var(--headerbar-height, var(--headerbar-default-height));
        background: var(--headerbar-background, var(--headerbar-default-background));
        padding: 0 var(--headerbar-side-padding, var(--headerbar-default-side-padding));

        & ol {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        & header {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            flex: 1 1 0;
            min-width: 0;
            height: 100%;
        }
        & .start,
        & .logo,
        & .content,
        & .end {
            height: 100%;
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
        }
        & .logo {
            margin-block: 0;
            margin-inline: 0 0.75rem;
            max-width: 7rem;
            min-width: 4rem;
            & a {
                height: 100%;
            }
            & img {
                height: 100%;
                max-width: 100%;
                object-fit: contain;
            }
        }
        & .content {
            flex-grow: 1;
            white-space: nowrap;
            overflow: hidden; /* TODO: ellipsis */
            & > :global(*) {
                /* slot children (esp. headings) may have unwanted margin: remove it */
                margin: 0;
            }
        }
        & .start {
            margin-block: 0;
            margin-inline: 0 0.75rem;
        }
        & .end {
            margin-block: 0;
            margin-inline: 0.75rem 0;

            /* Toggler and hidden action menu */
            & > li:last-child {
                position: relative;
                & ol {
                    position: absolute;
                    top: 100%;
                    inset-inline-end: 0;
                    flex-direction: column;
                    background: var(--color-bg-default);
                    border: var(--border-thin) solid var(--color-border-disabled);
                    box-shadow: 0 0 0.75rem 0.375rem rgba(0, 0, 0, 0.1);
                }
            }
        }
    }
    /* larger than mobile */
    @media screen and (--mq-minwidth-medium) {
        .headerbar {
            --headerbar-default-height: 7rem;
            --headerbar-default-side-padding: 2.5rem;

            & .start {
                flex: 0;
                flex-basis: 6rem;
                justify-content: center;
                margin-block: 0;
                margin-inline: 0 1.5rem;

                /* spacing between list items */
                & > li:not(:last-child) {
                    margin-block: 0;
                    margin-inline: 0 1rem;
                }
            }
            & .end {
                margin-block: 0;
                margin-inline: 1.5rem 0;

                /* spacing between list items */
                & > li:not(:last-child) {
                    margin-block: 0;
                    margin-inline: 0 1rem;
                }
            }
            & .logo {
                margin-block: 0;
                margin-inline: 0 1.5rem;
            }
        }
    }
    /* smaller than desktop */
    @media screen and (--mq-maxwidth-huge) {
        .headerbar {
            padding: 0 1rem;

            & .start {
                margin-block: 0;
                margin-inline: 0 1rem;
            }

            & .end {
                margin-block: 0;
                margin-inline: 1rem 0;
            }

            & .logo {
                display: none;
            }

            & header {
                /* here we handle special case if no start actions present but logo is */
                & :first-child {
                    &.logo {
                        margin: 0 1rem;
                        display: flex;
                    }
                }
            }
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} onclick={closeActionSubmenu} />

<div class="headerbar">
    <header>
        {#if startActions && startActions.length}
            <ol class="start">
                {#each startActions as buttonProps (buttonProps.key)}
                    <li>
                        <IconBarButton
                            {...buttonProps}
                            size={`base-${iconSizeResult}`}
                            icon={`${buttonProps.icon}-${iconSizeResult}`}
                            on:click={() => emitAction(buttonProps.key)} />
                    </li>
                {/each}
            </ol>
        {/if}
        {#if logoSrc}
            <div class="logo">
                {#if logoLinkUrl}
                    <a href={logoLinkUrl} target="_blank" rel="noopener noreferrer">
                        <img src={logoSrc} alt={logoAltText} />
                    </a>
                {:else}<img src={logoSrc} alt={logoAltText} />{/if}
            </div>
        {/if}
        <div class="content">
            {@render children?.()}
        </div>
    </header>
    <aside>
        <ol class="end">
            {#each visibleEndActions as buttonProps (buttonProps.key)}
                <li>
                    <IconBarButton
                        {...buttonProps}
                        size={`base-${iconSizeResult}`}
                        icon={`${buttonProps.icon}-${iconSizeResult}`}
                        on:click={() => emitAction(buttonProps.key)} />
                </li>
            {/each}
            {#if hiddenEndActions.length}
                <li>
                    <IconBarButton
                        label="More"
                        ariaLabel={false}
                        size={`base-${iconSizeResult}`}
                        icon={`more-${iconSizeResult}`}
                        on:click={toggleActionSubmenu} />
                    {#if hiddenActionsExpanded}
                        <ol>
                            {#each hiddenEndActions as buttonProps (buttonProps.key)}
                                <li>
                                    <IconBarButton
                                        {...buttonProps}
                                        size={`base-${iconSizeResult}`}
                                        icon={`${buttonProps.icon}-${iconSizeResult}`}
                                        on:click={() => emitAction(buttonProps.key)} />
                                </li>
                            {/each}
                        </ol>
                    {/if}
                </li>
            {/if}
        </ol>
        {@render asideAfter?.()}
    </aside>
</div>
